import * as React from 'react';
import { apiError } from '@/lib/api-error';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import BillingDetailsForm, { BillingValues } from '@/components/billing/BillingDetailsForm';
import { fetchBillingDetails, fetchBillingHistory, saveBillingDetails } from '@/data/billing';
import type { BillingChange, BillingDetails } from '@/types/billing';

const FIELD_LABEL: Record<string, string> = {
  legal_company_name: 'Legal company name',
  tax_id: 'VAT ID',
  registration_number: 'Registration number',
  billing_address_line1: 'Address',
  billing_address_line2: 'Address line 2',
  billing_city: 'City',
  billing_state: 'Region',
  billing_postal_code: 'Postal code',
  billing_country: 'Country',
  billing_contact_name: 'Billing contact',
  billing_contact_email: 'Billing email',
  billing_contact_phone: 'Phone',
  currency: 'Currency',
  payment_terms_days: 'Payment terms',
  invoice_delivery_method: 'Invoice delivery',
};

function when(iso: string) {
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function Spinner() {
  return (
    <div className="flex justify-center py-8">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  );
}

export function BillingSummary({ details, onEdit, whose }: { details: BillingDetails; onEdit: () => void; whose: string }) {
  const address = [details.billing_address_line1, details.billing_address_line2, [details.billing_postal_code, details.billing_city].filter(Boolean).join(' '), details.billing_state, details.billing_country].filter(Boolean);
  const rows: Array<[string, React.ReactNode]> = [
    ['Legal company name', details.legal_company_name],
    ['VAT ID', details.tax_id || '—'],
    ['Registration number', details.registration_number || '—'],
    ['Address', address.join(', ')],
    ['Billing contact', [details.billing_contact_name, details.billing_contact_email, details.billing_contact_phone].filter(Boolean).join(' · ')],
    ['Invoices', details.invoicing_status === 'approved'
      ? `Approved for invoice billing on ${details.approved_for_invoicing_at ? when(details.approved_for_invoicing_at) : 'record'}`
      : details.invoice_terms_accepted_at ? `Agreed on ${when(details.invoice_terms_accepted_at)}; BitSwan is approving ${whose === 'your' ? 'you' : 'them'} for invoice billing` : 'Not yet agreed'],
  ];
  return (
    <div className="space-y-4" data-testid="billing-summary">
      <dl className="divide-y">
        {rows.map(([label, value]) => (
          <div key={label} className="flex flex-wrap gap-x-6 gap-y-1 py-2.5">
            <dt className="w-44 shrink-0 text-sm text-muted-foreground">{label}</dt>
            <dd className="min-w-0 flex-1 text-sm">{value}</dd>
          </div>
        ))}
      </dl>
      <Button variant="outline" onClick={onEdit} data-testid="billing-edit">
        <Pencil className="mr-2 h-4 w-4" />
        Update billing details
      </Button>
    </div>
  );
}

export function BillingHistory({ entries, whose }: { entries: BillingChange[]; whose: string }) {
  if (entries.length === 0) {
    return <p className="py-6 text-center text-sm text-muted-foreground">None yet.</p>;
  }
  return (
    <ol className="space-y-4" data-testid="billing-history-list">
      {entries.map((entry) => (
        <li key={entry.id} className="rounded-md border p-3" data-testid={`billing-change-${entry.id}`}>
          <p className="text-sm">
            <span className="font-medium">{when(entry.changed_at)}</span>
            <span className="text-muted-foreground"> · {entry.changed_by_email || `someone in ${whose} organisation`} · {entry.first ? 'first entered' : `${Object.keys(entry.changes).length} field${Object.keys(entry.changes).length === 1 ? '' : 's'} changed`}</span>
          </p>
          {!entry.first && (
            <ul className="mt-2 space-y-1 text-sm">
              {Object.entries(entry.changes).map(([field, change]) => (
                <li key={field} className="flex flex-wrap gap-x-2">
                  <span className="w-40 shrink-0 text-muted-foreground">{FIELD_LABEL[field] ?? field}</span>
                  <span className="line-through decoration-muted-foreground/60 text-muted-foreground">{change.from || '—'}</span>
                  <span>→ {change.to || '—'}</span>
                </li>
              ))}
            </ul>
          )}
        </li>
      ))}
    </ol>
  );
}

export default function BillingDetailsSection({ orgId, whose = 'your', onBehalfOf }: { orgId?: string; whose?: string; onBehalfOf?: string }) {
  const queryClient = useQueryClient();
  const scope = orgId ?? 'own';
  const [editing, setEditing] = React.useState(false);
  const details = useQuery({ queryKey: ['billing-details', scope], queryFn: () => fetchBillingDetails(orgId) });
  const history = useQuery({ queryKey: ['billing-history', scope], queryFn: () => fetchBillingHistory(orgId) });

  const save = useMutation({
    mutationFn: (values: BillingValues) => saveBillingDetails(values, orgId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing-details', scope] });
      queryClient.invalidateQueries({ queryKey: ['billing-history', scope] });
      setEditing(false);
      toast.success('Billing details saved');
    },
    onError: (error) => toast.error(apiError(error, 'Could not save the billing details.')),
  });

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Billing details</CardTitle>
          <CardDescription>
            What appears on {whose} invoices. Changing it here does not change an
            invoice that has already been issued.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {details.isLoading ? (
            <Spinner />
          ) : details.data?.exists && !editing ? (
            <BillingSummary details={details.data} whose={whose} onEdit={() => setEditing(true)} />
          ) : (
            <div className="space-y-3">
              <BillingDetailsForm
                initial={details.data}
                onSubmit={(values) => save.mutateAsync(values).then(() => undefined)}
                submitting={save.isPending}
                onBehalfOf={onBehalfOf}
              />
              {details.data?.exists && (
                <Button variant="ghost" onClick={() => setEditing(false)} data-testid="billing-edit-cancel">Cancel</Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {details.data?.exists && (
        <Card data-testid="billing-history">
          <CardHeader>
            <CardTitle>Previous billing details</CardTitle>
          </CardHeader>
          <CardContent>
            {history.isLoading ? <Spinner /> : <BillingHistory entries={history.data ?? []} whose={whose} />}
          </CardContent>
        </Card>
      )}
    </>
  );
}
